import _ from 'lodash';
import { API_TYPES } from '@/main/app.constants';
import { PROPS_TO_DEHYDRATE } from '@/trendData/baseItem.constants';
import { BaseItemStore } from '@/trendData/baseItem.store';
import { ITEM_DATA_STATUS, ITEM_TYPES } from '@/trendData/trendData.constants';
import type { Item } from '@/trendData/baseItem.store';
import type { TableItem, TrendTableBinConfig } from '@/trendData/trendData.types';

interface TrendTablePersistedItem {
  id: string;
  name?: string;
  binConfig?: TrendTableBinConfig;
  stack?: boolean;
  outputUnits?: string;
  stat?: string;
  signalName?: string;
  categories?: string[];
  color?: string;
  calculationType?: string;
}

export type TrendTableDehydratedState = Pick<BaseItemStore<Item>, 'items'> & {
  items: TrendTablePersistedItem[];
};

export interface TrendTableResultsPayload {
  id: string;
  signalName: string;
  stat?: { key: string; timeUnits: string };
  tableData: unknown;
  categories?: string[];
}

export interface TrendTableBinPayload {
  id: string;
  bins: TrendTableBinConfig;
}

export class TrendTableStore extends BaseItemStore<TableItem> {
  static readonly storeName = 'sqTrendTableStore';

  get tables(): TableItem[] {
    return this.state.get('items');
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @returns {Object} The dehydrated items.
   */
  dehydrate(): TrendTableDehydratedState {
    const props = PROPS_TO_DEHYDRATE.concat(['binConfig', 'outputUnits', 'stack']);
    return {
      items: _.chain(this.state.get('items'))
        .filter(this.shouldDehydrateItem)
        .map((item) => _.pick(item, props) as TrendTablePersistedItem)
        .value(),
    };
  }

  /**
   * Rehydrates the tables.
   *
   * @param {Object} dehydratedState Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: TrendTableDehydratedState): void {
    this.state.set(
      'items',
      _.map(dehydratedState.items, (item) =>
        this.createTable(item.id, item.name ?? '', item.binConfig, _.omit(item, ['id', 'name']) as Partial<TableItem>),
      ),
    );
  }

  /**
   * Creates a Table item.
   *
   * @param {String} id - The id of the table
   * @param {String} name - The name of the table
   * @param {Object} [binConfig] - Object of properties for bins (sub-groups), if present
   * @param {Object[]} [props] - Array of properties to be set on the item.
   * @return {Object} an table item.
   */
  createTable(
    id: string,
    name: string,
    binConfig: TrendTableBinConfig | undefined,
    props: Partial<TableItem>,
  ): TableItem {
    _.assign(props, { type: API_TYPES.TABLE });
    return this.createItem(
      id,
      name,
      ITEM_TYPES.TABLE,
      _.assign(
        {
          data: [],
          dataStatus: ITEM_DATA_STATUS.INITIALIZING,
          statusMessage: '',
          stack: _.get(props, 'stack', false),
          binConfig,
        },
        props,
      ),
    ) as TableItem;
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    /**
     * Adds a Table item to the specified panel.
     *
     * @param {Object} payload - Object container
     * @param {String} payload.id- The id of the table
     * @param {String} payload.binConfig - binConfig for tables with more than one series
     * @param {String} payload.calculationType =- calculation type for this table
     * @param {String} [payload.color] - optional color.
     */
    TREND_ADD_TABLE: (payload: {
      id: string;
      name: string;
      binConfig?: TrendTableBinConfig;
      color?: string;
      calculationType?: string;
      stack?: boolean;
    }) => {
      this.state.push(
        'items',
        this.createTable(
          payload.id,
          payload.name,
          _.get(payload, 'binConfig', {}),
          _.pick(payload, ['color', 'calculationType', 'stack']),
        ),
      );
    },

    /**
     * Adds data once it's available.
     *
     * @param {Object} payload - Object container
     * @param {Number[]} payload.tableData - The data
     * @param {Number[]} payload.signalName - The name of the signal that is being aggregated (the y-axis)
     */
    TREND_TABLE_RESULTS_SUCCESS: (payload: TrendTableResultsPayload) => {
      const cursor = this.getItemCursor(payload.id);
      if (payload.stat) {
        cursor.set('stat', payload.stat.key);
      }
      cursor.set('data', payload.tableData);
      cursor.set('signalName', payload.signalName);
      if (payload.categories) {
        cursor.set('categories', payload.categories);
      }
    },

    /**
     * Sets properties on one or more table bins (aka sub-groups)
     *
     * @param {Object} payload - Object container
     * @param {String} payload.id - The id of the table
     * @param {String} payload.bins - Subgroups and associated properties to set
     * @param {String} payload.bins.id - name of bin
     * @param {String} payload.bins.color - color to set
     * @param {Boolean} payload.bins.visible - whether the bin is visible
     */
    TREND_TABLE_SET_BIN_PROPERTIES: (payload: TrendTableBinPayload) => {
      const cursor = this.getItemCursor(payload.id);
      cursor.set('binConfig', _.merge({}, cursor.get('binConfig'), payload.bins));
    },

    /**
     * Resets all bin configuration properties for a table
     *
     * @param {Object} payload - Object container
     * @param {String} payload.id - The id of the table
     */
    TREND_TABLE_RESET_BINS: (payload: { id: string }) => {
      const cursor = this.getItemCursor(payload.id);
      cursor.set('binConfig', {});
    },

    /**
     * Sets output units for the table
     *
     * @param {Object} payload - Object container
     * @param {String} payload.id - The id of the table
     * @param {String} payload.outputUnits - The output units
     */
    TREND_TABLE_SET_OUTPUT_UNITS: (payload: { id: string; outputUnits: string }) => {
      const cursor = this.getItemCursor(payload.id);
      cursor.set('outputUnits', payload.outputUnits);
    },
  };
}
